import { Alert, Button, Progress, Skeleton, Space, Tag, Typography } from 'antd';
import React from 'react';

import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { useAssessmentList, type AssessmentRowViewModel } from '@/hooks/useAssessmentList';
import { AddIcon, FilterIcon } from '@/icons';

const { Text, Title } = Typography;

// ── Status badge map ──────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'default' },
  in_progress: { label: 'In Progress', color: 'processing' },
  in_review: { label: 'In Review', color: 'warning' },
  complete: { label: 'Complete', color: 'success' },
};
const DEFAULT_BADGE = { label: 'Unknown', color: 'default' };

// ── Summary counts ────────────────────────────────────────────────────────────

/** Builds a compact summary string (e.g. "2 active · 1 complete") from the list of assessment rows. */
function buildSummary(items: AssessmentRowViewModel[]): string {
  const active = items.filter((i) => i.status === 'in_progress' || i.status === 'in_review').length;
  const complete = items.filter((i) => i.status === 'complete').length;
  const drafts = items.filter((i) => i.status === 'draft').length;
  const parts: string[] = [];
  if (active > 0) {
    parts.push(`${active} active`);
  }
  if (complete > 0) {
    parts.push(`${complete} complete`);
  }
  if (drafts > 0) {
    const draftSuffix = drafts === 1 ? '' : 's';
    parts.push(`${drafts} draft${draftSuffix}`);
  }
  return parts.join(' · ');
}

// ── Row ───────────────────────────────────────────────────────────────────────

/** Props for the `AssessmentRow` list-item button. */
interface AssessmentRowProps {
  item: AssessmentRowViewModel;
  onOpen: (assessmentId: string, flowStep: AssessmentFlowStep) => void;
}

/** Renders a single assessment card row with status badge, progress bar, and step label. */
function AssessmentRow({ item, onOpen }: Readonly<AssessmentRowProps>): React.ReactElement {
  const badge = item.isApproved
    ? { label: 'Approved', color: 'success' }
    : (STATUS_BADGE[item.status] ?? DEFAULT_BADGE);
  const initial = item.title.slice(0, 1).toUpperCase();
  const phaseLabel = `Step ${item.currentStep} of ${item.totalSteps}`;

  return (
    <button
      type="button"
      className="assessment-library__row"
      onClick={() => onOpen(item.assessmentId, item.flowStep)}
      style={{
        display: 'flex',
        gap: 12,
        padding: '16px 0',
        cursor: 'pointer',
        alignItems: 'center',
        width: '100%',
        border: 0,
        background: 'transparent',
        textAlign: 'left',
      }}
    >
      {/* Left: avatar + text */}
      <div
        style={{
          display: 'flex',
          width: 42,
          height: 42,
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          borderRadius: 8,
          background: 'var(--color-brand-900)',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {initial}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ display: 'block' }}>
          {item.title}
        </Text>
        <Space size={4} wrap style={{ marginTop: 2 }}>
          <Text type="secondary">Started {item.startedLabel}</Text>
          {item.lastModifiedBy !== null && item.lastModifiedBy !== undefined && (
            <Text type="secondary">· Lead: {item.lastModifiedBy}</Text>
          )}
        </Space>
      </div>

      {/* Right: badge + phase label + progress */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 6,
          minWidth: 160,
          flexShrink: 0,
        }}
      >
        <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
          {phaseLabel}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag color={badge.color} style={{ margin: 0 }}>
            {badge.label}
          </Tag>
          <Progress
            percent={item.completionPct}
            size="small"
            showInfo={false}
            style={{ width: 100, margin: 0 }}
          />
        </div>
      </div>
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/** Props for the `AssessmentLibrary` list component. */
export interface AssessmentLibraryProps {
  clientId: string;
  projectId: string;
  accessToken: string;
  onOpenAssessment: (assessmentId: string, flowStep: AssessmentFlowStep) => void;
  onNewAssessment: () => void;
}

/** Lists all assessments for a project with status badges, progress bars, and a "New Assessment" action. */
export function AssessmentLibrary({
  clientId,
  projectId,
  accessToken,
  onOpenAssessment,
  onNewAssessment,
}: Readonly<AssessmentLibraryProps>): React.ReactElement {
  const { items, loading, error } = useAssessmentList(accessToken, clientId, projectId);

  if (loading) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }

  if (error !== null && error !== undefined) {
    return <Alert type="error" message={error} showIcon />;
  }

  if (items.length === 0) {
    return (
      <Space
        direction="vertical"
        size="small"
        style={{ width: '100%', textAlign: 'center', padding: '32px 0' }}
      >
        <Text type="secondary">No assessments started yet.</Text>
        <Button type="primary" icon={<AddIcon />} onClick={onNewAssessment}>
          Start First Assessment
        </Button>
      </Space>
    );
  }

  const summary = buildSummary(items);

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Assessments
          </Title>
          {summary && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {summary}
            </Text>
          )}
        </div>
        <Space>
          <Button icon={<FilterIcon />}>Filter</Button>
          <Button type="primary" icon={<AddIcon />} onClick={onNewAssessment}>
            New Assessment
          </Button>
        </Space>
      </div>

      {/* List */}
      <div
        data-testid="assessment-library-list"
        style={{
          border: '1px solid var(--border-default, var(--ant-color-border, #d9d9d9))',
          borderRadius: 'var(--radius-base, 8px)',
          padding: '0 16px',
          backgroundColor: 'var(--bg-surface, var(--ant-color-bg-container, #ffffff))',
        }}
      >
        {items.map((item, index) => (
          <React.Fragment key={item.assessmentId}>
            <AssessmentRow item={item} onOpen={onOpenAssessment} />
            {index < items.length - 1 && (
              <div style={{ borderTop: '1px solid var(--ant-color-border, #d9d9d9)' }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </Space>
  );
}
